import { useMemo, useState } from "react";
import { api, ApiError, type User } from "../api";
import { btnPrimary } from "../icons";
import {
  BackButton,
  Caption,
  DESKTOP_CARD,
  Field,
  Subtitle,
  Title,
  mobileWrap,
} from "./auth-ui";

interface Props {
  isDesktop: boolean;
  onSignedUp: (user: User) => void;
  onSwitchToLogin: () => void;
}

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignupScreen({ isDesktop, onSignedUp, onSwitchToLogin }: Props) {
  const [step, setStep] = useState<0 | 1>(0);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [budget, setBudget] = useState("");

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailError = useMemo(() => {
    if (!email) return null;
    if (!EMAIL_RE.test(email.trim())) return "Enter a valid email";
    return null;
  }, [email]);

  const passwordError = useMemo(() => {
    if (!password) return null;
    if (password.length < 8) return "At least 8 characters";
    return null;
  }, [password]);

  const usernameError = useMemo(() => {
    if (!username) return null;
    if (username.length < 3) return "At least 3 characters";
    if (!USERNAME_RE.test(username)) return "Letters, numbers, underscore only";
    return null;
  }, [username]);

  const budgetNum = parseFloat(budget) || 0;
  const budgetError =
    budget && budgetNum <= 0 ? "Must be greater than 0" : null;

  const step0Ok =
    email.length > 0 &&
    password.length >= 8 &&
    !emailError &&
    !passwordError;
  const step1Ok =
    username.length >= 3 &&
    !usernameError &&
    budgetNum > 0 &&
    !budgetError;

  const submit = async () => {
    if (!step1Ok || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const user = await api.signup({
        email: email.trim(),
        password,
        username,
        budget: budgetNum,
      });
      onSignedUp(user);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === "EMAIL_TAKEN") {
          setServerError("That email is already in use");
          setStep(0);
        } else if (e.code === "USERNAME_TAKEN") {
          setServerError("Username already taken");
        } else {
          setServerError(e.message);
        }
      } else {
        setServerError(e instanceof Error ? e.message : "Sign up failed");
      }
      setSubmitting(false);
    }
  };

  return (
    <div style={isDesktop ? DESKTOP_CARD : mobileWrap("40px 24px 30px")}>
      {step === 1 && <BackButton onClick={() => setStep(0)} />}

      <div style={{ display: "flex", gap: 6, marginBottom: 32 }}>
        {[0, 1].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 999,
              background: i <= step ? "var(--ink)" : "var(--line)",
              transition: "background .25s",
            }}
          />
        ))}
      </div>

      {step === 0 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Caption>Step 1 of 2</Caption>
          <Title>Create your account</Title>
          <Subtitle>Use your email and a password to sign in later.</Subtitle>

          <Field
            label="Email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(v) => {
              setEmail(v);
              setTouched((t) => ({ ...t, email: true }));
              setServerError(null);
            }}
            placeholder="you@example.com"
            autoFocus
            error={touched.email ? emailError : null}
          />

          <Field
            label="Create password"
            type="password"
            value={password}
            onChange={(v) => {
              setPassword(v);
              setTouched((t) => ({ ...t, password: true }));
              setServerError(null);
            }}
            placeholder="At least 8 characters"
            error={touched.password ? passwordError : null}
            hint={
              touched.password || !password ? undefined : "At least 8 characters"
            }
          />

          {serverError && (
            <div style={{ marginTop: 4, marginBottom: 8, fontSize: 13, color: "var(--danger)" }}>
              {serverError}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            disabled={!step0Ok}
            onClick={() => step0Ok && setStep(1)}
            style={btnPrimary(step0Ok)}
          >
            Continue
          </button>

          <button
            onClick={onSwitchToLogin}
            style={{
              marginTop: 14,
              background: "transparent",
              border: "none",
              color: "var(--ink-2)",
              fontSize: 14,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Already have an account?{" "}
            <span style={{ color: "var(--ink)", fontWeight: 600 }}>Sign in</span>
          </button>
        </div>
      )}

      {step === 1 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Caption>Step 2 of 2</Caption>
          <Title>A few last details</Title>
          <Subtitle>Pick a username and set your monthly budget.</Subtitle>

          <Field
            label="Username"
            value={username}
            onChange={(v) => {
              setUsername(v.toLowerCase().replace(/\s/g, ""));
              setTouched((t) => ({ ...t, username: true }));
              setServerError(null);
            }}
            placeholder="yourname"
            autoFocus
            error={touched.username ? usernameError : null}
            hint={
              touched.username || !username
                ? undefined
                : "3–20 chars, letters / numbers / _"
            }
            prefix={<span style={{ color: "var(--muted)", fontSize: 16 }}>@</span>}
          />

          <Field
            label="Monthly budget"
            type="number"
            inputMode="decimal"
            value={budget}
            onChange={(v) => {
              setBudget(v);
              setTouched((t) => ({ ...t, budget: true }));
            }}
            placeholder="0"
            error={touched.budget ? budgetError : null}
            prefix={<span style={{ color: "var(--muted)", fontSize: 16 }}>€</span>}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            {[500, 800, 1200, 1500, 2000].map((v) => (
              <button
                key={v}
                onClick={() => {
                  setBudget(String(v));
                  setTouched((t) => ({ ...t, budget: true }));
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 500,
                  background: budgetNum === v ? "var(--ink)" : "var(--surface)",
                  color: budgetNum === v ? "#fff" : "var(--ink-2)",
                  border:
                    "1px solid " +
                    (budgetNum === v ? "var(--ink)" : "var(--line)"),
                }}
              >
                €{v}
              </button>
            ))}
          </div>

          {serverError && (
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)" }}>
              {serverError}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            disabled={!step1Ok || submitting}
            onClick={submit}
            style={btnPrimary(step1Ok && !submitting)}
          >
            {submitting ? "Creating…" : "Create account"}
          </button>
        </div>
      )}
    </div>
  );
}
